import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  selectClientFetchId,
  selectClientIsLoadingData,
  selectClientIsSubscribedToBuy,
  selectClientIsSubscribedToHome,
  selectClientIsSubscribedToHomebotListings,
  selectClientIsSubscribedToHomeMarket,
  selectClientIsUnsubscribedFromCustomer,
  selectClientRequestedUnsubscribe
} from '../../../store/selectors/client'
import {
  setClientIsSubscribedToBuy,
  setClientIsSubscribedToHome,
  setClientIsSubscribedToHomeMarket,
  setClientIsSubscribedToHomeownerEmails,
  setClientIsSubscribedToListings,
  setClientIsUnsubscribedFromCustomer
} from '../../../actions/client'
import { selectCustomerProfileLoading } from '../../../store/selectors/customerProfile'
import { getSmsLongCode } from '../../../actions/clientEvents'
import { Button, If, Unless, Text, OverlayPanel } from '../..'
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import {
  unsubscribeFromCustomerFlow,
  unsubscribeFromHomeDigestFlow,
  unsubscribeFromHomeMarketFlow,
  unsubscribeFromHomeownerEmailsFlow,
  unsubscribeFromListingsFlow
} from '../../../constants/unsubscribe'
import history from '../../../history'
import Loading from '../Loading/LoadingApp'

import PreferencesPanel from '../PreferencesPanel'
import TextUpdatesPanel from '../TextUpdatesPanel'
import EmailUpdatesPanel from '../EmailUpdatesPanel'

import styles from './Subscription.module.scss'
import { useTrackingContext } from '../../../providers/tracking'

const propTypes = {
  reportType: PropTypes.oneOf(['home', 'buy']).isRequired,
  forBuyer: PropTypes.bool
}
const Subscription = ({ reportType }) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()

  const customerProfileLoading = useSelector(selectCustomerProfileLoading)
  const clientDataLoading = useSelector(selectClientIsLoadingData)

  const clientId = useSelector(selectClientFetchId)
  const flagForUnsub = useSelector(selectClientRequestedUnsubscribe)

  const subscribedToBuy = useSelector(selectClientIsSubscribedToBuy)
  const subscribedToHome = useSelector(selectClientIsSubscribedToHome)
  const subscribedToHomeMarket = useSelector(selectClientIsSubscribedToHomeMarket)
  const subscribedToListings = useSelector(selectClientIsSubscribedToHomebotListings)
  const unsubscribedFromCustomer = useSelector(selectClientIsUnsubscribedFromCustomer)

  const forUnsubscribeFromCustomer = flagForUnsub === unsubscribeFromCustomerFlow

  const forBuyer = reportType === 'buy' && !forUnsubscribeFromCustomer
  const forListings = flagForUnsub === unsubscribeFromListingsFlow
  const forMarket = flagForUnsub === unsubscribeFromHomeMarketFlow
  const forHomeownerEmails = flagForUnsub === unsubscribeFromHomeownerEmailsFlow
  const forHomeDigest = flagForUnsub === unsubscribeFromHomeDigestFlow

  let isSubscribed
  if (forUnsubscribeFromCustomer) {
    isSubscribed = !unsubscribedFromCustomer
  } else if (forListings) {
    isSubscribed = subscribedToListings
  } else if (forBuyer) {
    isSubscribed = subscribedToBuy
  } else if (forMarket) {
    isSubscribed = subscribedToHomeMarket
  } else {
    isSubscribed = subscribedToHome
  }

  const navToPreferences = () => {
    trackingClient?.webPreferencesManaged()
    history.push(OVERLAY_HELPERS.preferences.buildPath())
  }

  const toggleSubscribe = force => {
    const setTo = typeof force === 'boolean' ? force : !isSubscribed

    // handle web unsubscribe, not from email link
    if (isSubscribed && typeof force !== 'boolean') {
      trackingClient?.webUnsubscribed()
    }

    if (forListings) {
      dispatch(setClientIsSubscribedToListings({ setTo, notify: true }))
    } else if (forBuyer) {
      dispatch(setClientIsSubscribedToBuy({ setTo, notify: true }))
    } else if (forMarket) {
      dispatch(setClientIsSubscribedToHomeMarket({ setTo, notify: true }))
    } else if (forHomeownerEmails) {
      dispatch(setClientIsSubscribedToHomeownerEmails({ setTo, notify: true }))
    } else if (forHomeDigest) {
      dispatch(setClientIsSubscribedToHome({ setTo, notify: true }))
    } else if (forUnsubscribeFromCustomer) {
      dispatch(setClientIsUnsubscribedFromCustomer({ setTo, notify: true }))
    } else {
      dispatch(setClientIsSubscribedToHome({ setTo, notify: true }))
    }
  }

  useEffect(() => {
    if (clientId) {
      dispatch(getSmsLongCode(clientId))

      if (flagForUnsub) {
        const force = forUnsubscribeFromCustomer ? true : false

        toggleSubscribe(force)
      }
    }
  }, [clientId])

  return (
    <div className={classnames('card card--standalone', styles.container)}>
      <Text element='h3' weight='light' size='l' paragraph variant='neutralCool' variantCount='900'>
        <If condition={isSubscribed}>
          <If condition={forBuyer}>
            <FormattedMessage
              id='Unsubscribe.buyer.headline'
              defaultMessage='Not enjoying your monthly Market report?'
            />
          </If>
          <Unless condition={forBuyer}>
            <FormattedMessage
              id='Unsubscribe.homeonwer.headline'
              defaultMessage='Not enjoying your monthly Home report?'
            />
          </Unless>
        </If>
        <Unless condition={isSubscribed}>
          <If condition={forBuyer}>
            <FormattedMessage
              id='Subscribe.headline'
              defaultMessage='Get a Homebot Market report in your inbox every month.'
            />
          </If>
          <Unless condition={forBuyer}>
            <FormattedMessage
              id='Subscribe.homeowner.headline'
              defaultMessage='Get a Homebot Home report in your inbox every month.'
            />
          </Unless>
        </Unless>
      </Text>
      <Button className={styles.linkButton} onClick={navToPreferences}>
        <FormattedMessage id='Subscribe.homeowner.manage' defaultMessage='Manage preferences' />
      </Button>
      {clientDataLoading || customerProfileLoading || flagForUnsub ? (
        <Loading />
      ) : (
        <OverlayPanel
          back={OVERLAY_HELPERS.preferences.getOrigin()}
          path={OVERLAY_HELPERS.preferences.buildPath()}
          title={OVERLAY_HELPERS.getTitle('preferences', intl.formatMessage)}
          disableAnchor
          component={PreferencesPanel}
        />
      )}
      <OverlayPanel
        back={OVERLAY_HELPERS.textUpdates.getOrigin()}
        path={OVERLAY_HELPERS.textUpdates.buildPath()}
        title={OVERLAY_HELPERS.getTitle('textUpdates', intl.formatMessage)}
        disableAnchor
        component={TextUpdatesPanel}
      />
      <OverlayPanel
        back={OVERLAY_HELPERS.emailUpdates.getOrigin()}
        path={OVERLAY_HELPERS.emailUpdates.buildPath()}
        title={OVERLAY_HELPERS.getTitle('emailUpdates', intl.formatMessage)}
        disableAnchor
        component={EmailUpdatesPanel}
      />
      <Button
        className={styles.linkButton}
        onClick={toggleSubscribe}
        data-qa={isSubscribed ? 'subscribe-button' : 'unsubscribe-button'}
      >
        <If condition={isSubscribed}>
          <FormattedMessage id='Unsubscribe.link' defaultMessage='Unsubscribe' />
        </If>
        <Unless condition={isSubscribed}>
          <If condition={forBuyer}>
            <FormattedMessage id='Subscribe.buyer.link' defaultMessage='Subscribe to the monthly Market report' />
          </If>
          <If condition={!forBuyer}>
            <FormattedMessage id='Subscribe.homeowner.link' defaultMessage='Subscribe to the monthly Home report' />
          </If>
        </Unless>
      </Button>
    </div>
  )
}

Subscription.propTypes = propTypes

export default Subscription
